import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { companyInfo } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function DobbeltportePage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const sliderRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const hasDraggedRef = useRef(false);

  const dobbeltportProjects = [
    {
      src: "/_assets/dobbeltport-01.webp",
      title: "Galvaniseret gitterport ved erhverv",
      category: "Erhverv & Industri",
      desc: "Stor galvaniseret metalport med tråd-/gitterstruktur udført som en integreret del af en større indhegning ved erhvervsbygning."
    },
    {
      src: "/_assets/dobbeltport-02.webp",
      title: "Dobbeltport med gitterfyldning",
      category: "Adgangsport",
      desc: "Galvaniseret to-fløjet port monteret ved ejendom med HOVENDALS montagebil på pladsen."
    },
    {
      src: "/_assets/dobbeltport-03.webp",
      title: "Mørkegrå port med geometriske felter",
      category: "Privat Ejendom",
      desc: "Bred, eksklusiv portkonstruktion med geometrisk opdelte felter foran en privat ejendom."
    },
    {
      src: "/_assets/pxl-20260705-162325452.jpg",
      title: "To-fløjet smedejernsport",
      category: "Specialfremstillet",
      desc: "Symmetrisk port udført i kraftigt galvaniseret stål med tilhørende stolper."
    }
  ];

  const slideLeft = () => {
    if (sliderRef.current) sliderRef.current.scrollBy({ left: -420, behavior: 'smooth' });
  };

  const slideRight = () => {
    if (sliderRef.current) sliderRef.current.scrollBy({ left: 420, behavior: 'smooth' });
  };

  const handleMouseDown = (e) => {
    if (!sliderRef.current) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    startXRef.current = e.pageX - sliderRef.current.offsetLeft;
    scrollLeftRef.current = sliderRef.current.scrollLeft;
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !sliderRef.current) return;
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 1.4;
    sliderRef.current.scrollLeft = scrollLeftRef.current - walk;
    if (Math.abs(walk) > 6) hasDraggedRef.current = true;
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  return (
    <>
      <SEO
        title="Dobbeltporte | HOVENDALS ApS"
        description="Portløsninger med fokus på funktionalitet, kvalitet og et gennemført design. Skræddersyede dobbeltporte til private og erhverv i hele Danmark."
        ogImage="/_assets/dobbeltport-03.webp"
      />

      {/* Hero Section med stort visuelt produktbillede */}
      <section className="relative py-24 md:py-32 bg-[#F3F4F7] border-b border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] block">
                Porte & Adgangskontrol
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#143463] tracking-tight leading-[1.08]">
                Dobbeltporte
              </h1>
              <p className="text-lg sm:text-xl text-[#4B515D] font-light leading-relaxed max-w-xl">
                Portløsninger med fokus på funktionalitet, kvalitet og et gennemført design.
              </p>
              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="px-8 py-4 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors flex items-center gap-2"
                >
                  <span>Indhent tilbud</span>
                  <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-8 py-4 bg-white border border-[#D6D3CE] text-[#143463] hover:text-[#004FA4] hover:border-[#004FA4] font-medium text-xs tracking-wider uppercase transition-colors flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-phone text-[#004FA4]" aria-hidden="true"></i>
                  <span>Ring {companyInfo.phone}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative overflow-hidden bg-white border border-[#D6D3CE]/70 shadow-sm">
                <img
                  src={imageUrl("/_assets/dobbeltport-03.webp", "width=1200,quality=85,format=auto")}
                  alt="Bred mørkegrå dobbeltport foran privat ejendom"
                  className="w-full h-auto aspect-[16/10] object-cover"
                />
                <div className="p-4 bg-white/95 border-t border-[#D6D3CE]/50 flex items-center justify-between text-xs">
                  <span className="font-medium text-[#143463]">Reference:</span>
                  <span className="text-[#4B515D] font-light">Mørkegrå portkonstruktion med geometriske felter</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 1 & 2: Introduktion og produktpræsentation (Type A layout) */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Stort billede til venstre (~60%) */}
            <div className="lg:col-span-7">
              <div className="group relative overflow-hidden bg-[#F3F4F7] border border-[#D6D3CE]/70 shadow-sm">
                <img
                  src={imageUrl("/_assets/dobbeltport-01.webp", "width=1300,quality=85,format=auto")}
                  alt="Stor galvaniseret metalport ved erhvervsbygning"
                  className="w-full h-auto aspect-[16/10] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
                <div className="p-4 bg-white/95 border-t border-[#D6D3CE]/50 flex items-center justify-between text-xs">
                  <span className="font-medium text-[#143463]">Dokumenteret projekt:</span>
                  <span className="text-[#4B515D] font-light">Galvaniseret dobbeltport ved erhvervsejendom</span>
                </div>
              </div>
            </div>

            {/* Tekstområde til højre (~40%) */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] block">
                Klassisk elegance & sikkerhed
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#143463] tracking-tight leading-[1.12]">
                En præsentabel indgang til din ejendom
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  En dobbeltport er den ideelle løsning til brede indkørsler og adgangsveje, hvor man ønsker en symmetrisk, elegant åbning og maksimal passagebredde.
                </p>
                <p>
                  Hos HOVENDALS fremstiller og leverer vi portløsninger, der er nøje afstemt efter ejendommens hegn, murværk eller terrænforhold. Portene tilpasses i bredde og højde, så de matcher kundens konkrete behov for passage af biler, trailere og maskiner.
                </p>
                <p>
                  Vi leverer og monterer i hele Danmark til både private villaer, landejendomme og virksomheder.
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/porte"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F] group"
                >
                  <span>Se alle vores porte og skydeporte</span>
                  <i className="fa-sharp fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 3: Horisontalt Galleri & Projekter (WIŚNIOWSKI slider) */}
      <section className="py-24 md:py-32 bg-[#F3F4F7] border-t border-b border-[#D6D3CE]/60 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] block mb-2">
                Dokumenterede Løsninger
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#143463] tracking-tight">
                Udførte portprojekter i hele landet
              </h2>
              <p className="text-[#4B515D] font-light text-base mt-2 max-w-2xl">
                Se eksempler på udførte dobbeltporte med fokus på robust konstruktion, let gang og flot finish.
              </p>
            </div>

            {/* Navigationspile */}
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={slideLeft}
                aria-label="Forrige billede"
                className="w-12 h-12 border border-[#D6D3CE] hover:border-[#004FA4] hover:text-[#004FA4] text-[#143463] flex items-center justify-center transition-colors cursor-pointer bg-white"
              >
                <i className="fa-sharp fa-solid fa-arrow-left text-sm" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={slideRight}
                aria-label="Næste billede"
                className="w-12 h-12 border border-[#D6D3CE] hover:border-[#004FA4] hover:text-[#004FA4] text-[#143463] flex items-center justify-center transition-colors cursor-pointer bg-white"
              >
                <i className="fa-sharp fa-solid fa-arrow-right text-sm" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div
            ref={sliderRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            className="flex gap-6 overflow-x-auto scroll-smooth pb-4 no-scrollbar cursor-grab active:cursor-grabbing select-none"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {dobbeltportProjects.map((item, idx) => (
              <div
                key={item.src}
                onClick={() => {
                  if (!hasDraggedRef.current) setLightboxIndex(idx);
                }}
                style={{ scrollSnapAlign: 'start' }}
                className="group shrink-0 w-[320px] sm:w-[400px] lg:w-[460px] bg-white border border-[#D6D3CE]/70 flex flex-col cursor-pointer overflow-hidden transition-all duration-300 hover:border-[#004FA4] hover:shadow-lg"
              >
                <div className="relative aspect-[16/11] overflow-hidden bg-[#F3F4F7]">
                  <img
                    src={imageUrl(item.src, "width=900,quality=85,format=auto")}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-600 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="absolute top-3.5 left-3.5 bg-[#143463]/90 text-white text-[11px] font-medium px-3 py-1 uppercase tracking-wider">
                    {item.category}
                  </div>
                </div>

                <div className="p-6 flex flex-col justify-between flex-grow">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-light text-[#143463] group-hover:text-[#004FA4] transition-colors flex items-center justify-between">
                      <span>{item.title}</span>
                      <i className="fa-sharp fa-solid fa-arrow-right text-xs text-[#004FA4] transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true"></i>
                    </h3>
                    <p className="text-sm text-[#4B515D] font-light mt-2.5 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <div className="pt-4 mt-5 border-t border-[#F3F4F7] flex items-center justify-between">
                    <span className="text-xs font-medium text-[#004FA4] uppercase tracking-wider">
                      Se fuld størrelse &rarr;
                    </span>
                    <span className="text-xs text-[#4B515D] font-light">
                      HOVENDALS
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Sektion 4: Kontakt / Tilbud */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#143463] text-white p-10 sm:p-16 lg:p-20 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl space-y-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF] block">
                Uforpligtende tilbud
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight">
                Skal vi levere din nye dobbeltport?
              </h2>
              <p className="text-base text-slate-300 font-light leading-relaxed">
                Kontakt os i dag for at høre mere om mulighederne og få et uforpligtende tilbud inklusiv professionel rådgivning og montage.
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row gap-3">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
              >
                Indhent tilbud
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-8 py-4 bg-transparent border border-white/40 hover:border-white text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
              >
                Ring {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <QuoteSection />

      {lightboxIndex !== null && (
        <LightboxModal
          images={dobbeltportProjects}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </>
  );
}
